import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { connect } from 'react-redux'
import { compose } from 'recompose'
import history from 'browserHistory'
import dateFns from 'date-fns'

import { selectors as OrganizationsSelectors } from 'data/organizations'
import { selectors as OrganizationResourcesUsageSelectors } from 'data/organizationResourcesUsage'
import { selectors as PaymentSelectors } from 'data/payment'

import providesOrganizationResourcesUsage from 'components/Organizations/dataSync/providesResourcesUsage'
import { Badge, Text, Button } from 'components/base'
import ErrorMessage from '../components/ErrorMessage'
import StatusCard from './StatusCard'
import PlanDescription from '../components/PlanDescription'
import CurrentPlanDetails from './CurrentPlanDetails'
import IfPaidPlan from 'components/Utils/IfPaidPlan'
import * as Icons from 'components/Utils/Icons'

import providesOrganizationPaymentInfos from '../../dataSync/providesOrganizationPaymentInfos'
import RoutesHelper from 'helpers/RoutesHelper'
import { centsToDollars } from '../utils'

class SettingsBillingOverview extends Component {
  static propTypes = {
    organization: PropTypes.object.isRequired,
    resourcesUsage: PropTypes.object.isRequired,
  }

  handleProductPlanChangeClick = event => {
    event.preventDefault()
    history.replace(RoutesHelper.settingsOrganizationBillingUpgrade())
  }

  handlePaymentMethodClick = event => {
    event.preventDefault()
    history.replace(RoutesHelper.settingsOrganizationBillingPaymentMethod())
  }

  render() {
    if (this.props.paymentInfoFetchState.error) {
      return (
        <ErrorMessage
          retry={this.props.forceQuery}
          action="getting your billing information"
          message={this.props.paymentInfoFetchState.error.message}
        />
      )
    }

    if (
      this.props.paymentInfoFetchState.loading ||
      this.props.paymentInfoFetchState.pristine
    ) {
      return <div>Loading your billing information...</div>
    }

    const { organization } = this.props
    return (
      <section className="padding-vertical-large">
        {this.renderMessage()}

        <div className="flexbox padding-bottom-large flexbox-space-between flexbox-align-center">
          <Text.Subtitle>
            Your team is on the
            <span className="capitalize"> {organization.product} </span>
            Plan
            <IfPaidPlan show={false}>
              <Text.PrimaryAction
                href={RoutesHelper.settingsOrganizationBillingUpgrade()}
                onClick={this.handleProductPlanChangeClick}
                className="margin-left-small"
              >
                upgrade
              </Text.PrimaryAction>
            </IfPaidPlan>
          </Text.Subtitle>
          {this.props.subscription &&
            this.props.subscription.trialEndAt && (
              <Badge className="height-full">
                Free month expiring{' '}
                {dateFns.format(
                  this.props.subscription.trialEndAt,
                  'Do, MMM YYYY'
                )}
              </Badge>
            )}
        </div>
        {this.renderUsageCards()}
        {organization.product === 'free'
          ? this.renderFree()
          : this.renderPaying()}
      </section>
    )
  }

  renderUsageCards = () => {
    const { organization } = this.props
    const { notes, storage } = this.props.resourcesUsage.toJS()

    return (
      <div className="flexbox flexbox-wrap margin-bottom-huge ">
        <IfPaidPlan show={false}>
          <StatusCard
            title="Notes usage"
            Icon={Icons.NoteIcon}
            current={notes.current}
            limit={notes.limit}
            unit="notes"
            description="On the Free plan, your team can create up to 50 shared docs over a 30 rolling-day period"
          />

          <StatusCard
            title="Available storage"
            Icon={Icons.ArchiveIcon}
            current={(storage.current / 1e9).toFixed(1)}
            limit={storage.limit / 1e9}
            unit="GB"
            description="On the Free plan, your images and attachments storage is limited."
          />
        </IfPaidPlan>
        <StatusCard
          title="Active members"
          Icon={Icons.PeopleIcon}
          current={organization.activeUsersCount}
          limit={organization.usersCount}
          unit="members"
          description={
            <Text.ExternalLink
              target="_blank"
              rel="noopener noreferrer"
              href="https://help.slite.com/pricing/what-is-slites-fair-billing-policy"
            >
              Learn more
            </Text.ExternalLink>
          }
        />

        <StatusCard
          title="Available credit"
          Icon={Icons.SparklesIcon}
          current={centsToDollars(this.props.paymentInfos.credit)}
          description={'For future payment'}
        />
      </div>
    )
  }

  renderFree() {
    return (
      <div style={{ width: 560 }}>
        <PlanDescription>
          <div className="margin-bottom-normal">
            <div className="">
              from{' '}
              <span
                style={{ fontSize: 32 }}
                className="shade-05 font-weight-bold "
              >
                $6.67
              </span>
              <Text.Detail inline> /user/month</Text.Detail>
            </div>
          </div>
          <Button.Primary
            className="margin-bottom-large width-full"
            onClick={this.handleProductPlanChangeClick}
          >
            Upgrade
          </Button.Primary>
        </PlanDescription>
      </div>
    )
  }

  renderPaying() {
    return (
      <CurrentPlanDetails
        activeUsersCount={this.props.organization.activeUsersCount}
        plan={this.props.plan}
        coupon={this.props.coupon}
        subscription={this.props.subscription}
        organization={this.props.organization}
      />
    )
  }

  renderMessage() {
    const messageId = new URLSearchParams(window.location.search).get(
      'messageId'
    )
    if (!messageId) {
      return null
    }
    if (messageId === 'subscriptionSuccess') {
      return (
        <div className="margin-vertical-large radius-normal background-green shade-100 padding-horizontal-large padding-vertical-medium">
          <div className="font-weight-bold margin-bottom-smaller">
            You've successfully upgraded your team plan{' '}
            <span role="img" aria-label="party-emoji">
              🎉
            </span>
          </div>
          You can start enjoying it now!
        </div>
      )
    }

    if (messageId === 'cancelSuccess') {
      return (
        <div className="margin-vertical-large radius-normal background-teal shade-100 padding-horizontal-large padding-vertical-medium">
          <div className="font-weight-bold margin-bottom-smaller">
            You've successfully downgraded your team from the{' '}
            <span className="capitalize">
              {this.props.organization.product}
            </span>{' '}
            plan. Your team will be on the Free plan starting{' '}
            {dateFns.format(
              this.props.subscription.canceledAt,
              'MMMM Do, YYYY'
            )}
            .
          </div>
        </div>
      )
    }
  }
}

const connected = connect(state => {
  return {
    organization: OrganizationsSelectors.getOrganization(state),
    resourcesUsage: OrganizationResourcesUsageSelectors.getOrganizationResourcesUsage(
      state
    ),
    plan: PaymentSelectors.getOrganizationPlan(state),
    paymentInfos: PaymentSelectors.getOrganizationPaymentInfos(state),
    coupon: PaymentSelectors.getOrganizationCoupon(state),
    subscription: PaymentSelectors.getOrganizationSubscription(state),
  }
})

export default compose(
  providesOrganizationResourcesUsage,
  providesOrganizationPaymentInfos,
  connected
)(SettingsBillingOverview)
